import { useEffect, useState, useContext } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import axios from 'axios';
import { usePostHog } from '@posthog/react';

import { ChevronDown, CheckCircle } from "../../files/icons/index.js";

import { EditableDatePicker } from '../components/datePicker.js';
import MaskedInput from "../components/maskedInput.js";

import { AppContext } from '../../context.js';

import Loading from '../components/loading.js';
import Helmet from "../components/helmet.js";

import { Button, Status } from '../components/component.js';

function ReferralCreate() {
  const context = useContext(AppContext);
  const posthog = usePostHog();

  const [searchParams] = useSearchParams();

  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);

  const [settings, setSettings] = useState(null);

  const [template, setTemplate] = useState(null);

  const [importMode, setImportMode] = useState("single");
  const [selectedFile, setSelectedFile] = useState(null);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");

  const [form, setForm] = useState({});

  useEffect(() => {
    let u = context.addSpiner();
    axios.get('/api/admin/settings', {
    }).then((settings) => {
      setSettings(settings.data);
      setTemplate(settings.data.referralTemplates[0]);

      setLoading(false);
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }, []);

  function templateChanged(templateID) {
    let selectedTemplate = settings.referralTemplates.find((t) => t.id === templateID);
    setTemplate(selectedTemplate);
  }

  function renderPages() {
    return template.pages.map((page) => {
      return (
        <div key={page.id} className="card mb-2">
          <div className="card-header sm">
            <div className="typo-strong-175">{page.title}</div>
          </div>
          <div className="card-content sm">
            {page.lines.map((line, index) => renderLine(page.id, line, page.lines.length === index + 1))}
          </div>
        </div>
      );
    });
  }

  function renderLine(pageID, line, isLastLine) {
    return (
      <div key={"page_"+pageID+"_line_"+line.id} className={"row " + (isLastLine ? "" : "mb-2")}>
        {line.map((lineField, index) => {
          let field = template.fields.find((f) => f.code === lineField.code);

          return (
            <div key={"page_"+pageID+"_line_"+line.id+"_field_"+field.code} className={`col-xs-${lineField.size} py-0`}>
              <div className="typo-strong-150">{field.title}:</div>

              {field.type === "text" && !field.format && (
                <input type="text" placeholder="" value={form[field.code]} onChange={(e) => changeField(field.code, e.target.value)} className="input sm typo-normal-150 w-100" />
              )}

              {field.type === "text" && field.format && (
                <MaskedInput
                  value={form[field.code]}
                  onChange={(value) => changeField(field.code, value)}
                  formatRegex={field.format}
                  className="input sm typo-normal-150 w-100"
                />
              )}

              {field.type === "date" && (
                <EditableDatePicker 
                  date={form[field.code]}
                  onChange={(e) => changeField(field.code, e)}
                  rightIcon={<ChevronDown height={16} width={16} color={'grey'} />}
                  defaultLevel="year"
                />
              )}

              {field.type === "radio" && (
                field.options.map((option) => {
                  return (
                    <div key={"radio_"+field.code+"_"+option} className={"item typo-normal-150 me-2 " + (form[field.code] == option ? "blue" : "grey")} onClick={(e) => changeField(field.code, option)}>{option}</div>
                  );
                })
              )}
            </div>
          );
        })}
      </div>
    );
  }

  function changeField(name, value) {
    const newForm = { ...form, [name]: value ? value : null };
    setForm(newForm);
  }

  function handleFileChange(e) {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      // Reset the file input so subsequent uploads trigger onChange
      e.target.value = '';
    }
  }

  function downloadTemplate(format) {
    let u = context.addSpiner();
    axios.get(`/api/partner/referrals/template/${template.id}?format=${format}`, {
      responseType: 'blob'
    }).then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const fileName = response.headers['content-disposition']?.split('filename=')[1]?.replace(/"/g, '') || `referral_template_${template.id}.${format}`;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    }).catch((e) => {
      context.addError(e);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function importFile() {
    if (!selectedFile) {
      context.addError(new Error("Please select a file"));
      return;
    }

    let u = context.addSpiner();
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('templateID', template.id);

    axios.post('/api/partner/referrals/import', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    }).then((response) => {
      navigate(`/referrals?importID=${response.data.importID}`);
    }).catch((e) => {
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  function create() {
    let u = context.addSpiner();

    axios.post('/api/partner/referrals/create', {
      templateID: template.id,
      firstName: firstName,
      lastName: lastName,
      email: email,
      phone: phone,
      message: message,
      form: form,
    }).then((response) => {
      posthog?.capture('partner_referral_created', {
        referral_id: response.data.id,
        template_id: template.id,
      });
      navigate('/referral/'+response.data.id);
    }).catch((e) => {
      posthog?.capture('partner_referral_create_failed', {
        template_id: template.id,
        status: e?.response?.status ?? 0,
        code: e?.response?.data?.code ?? null,
      });
      context.addError(e);
      context.removeSpiner(u);
    }).finally(() => {
      context.removeSpiner(u);
    });
  }

  if (loading) {
    return <Loading />;
  }

  return (
    <>
      <Helmet title="Referral Create" />

      <div className="container-fluid">

        <div className="row mb-4">
          <div className="col-xs-12 col-lg-offset-3 col-lg-6">
            <span className="typo-hero-small">Referral Create</span>
          </div>
        </div>

        {/* Referral Template */}
        <div className="row">
          <div className="col-xs-12 col-lg-offset-3 col-lg-6">
            <span className="typo-strong-200">Template</span>
          </div>
        </div>

        <div className="row mb-4">
          <div className="col-xs-12 col-lg-offset-3 col-lg-6">
            <div className="row">
              {settings.referralTemplates.map((t) => {
                return (
                  <div key={"template_"+t.id} className="col-xs-4">
                    <div className="card clickable mb-2" onClick={() => templateChanged(t.id)}>
                      <div className="card-header sm">
                        <div className="d-flex justify-content-between align-items-center">
                          <div className="w-100">
                            <div className="typo-strong-175">{t.title}</div>
                            <div className="typo-normal-150">{t.description}</div>
                          </div>
                          {template.id === t.id && (
                            <CheckCircle height={24} width={24} color={'grey'} />
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        {/* Import Mode Selector */}
        <div className="row">
          <div className="col-xs-12 col-lg-offset-3 col-lg-6">
            <span className="typo-strong-200">Import Mode</span>
          </div>
        </div>

        <div className="row mb-4">
          <div className="col-xs-12 col-lg-offset-3 col-lg-6">
            <div className="row">
              <div className="col-xs-4">
                <div className="card clickable mb-2" onClick={() => setImportMode("single")}>
                  <div className="card-header sm">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="w-100">
                        <div className="typo-strong-175">Single Lead</div>
                        <div className="typo-normal-150">Create one referral</div>
                      </div>
                      {importMode === "single" && (
                        <CheckCircle height={24} width={24} color={'grey'} />
                      )}
                    </div>
                  </div>
                </div>
              </div>
              <div className="col-xs-4">
                <div className="card clickable mb-2" onClick={() => setImportMode("import")}>
                  <div className="card-header sm">
                    <div className="d-flex justify-content-between align-items-center">
                      <div className="w-100">
                        <div className="typo-strong-175">Import File</div>
                        <div className="typo-normal-150">Import multiple referrals</div>
                      </div>
                      {importMode === "import" && (
                        <CheckCircle height={24} width={24} color={'grey'} />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Referral Form */}
        {importMode === "single" && (
          <>
            <div className="row">
              <div className="col-xs-12 col-lg-offset-3 col-lg-6">
                <span className="typo-strong-200">Referral Data</span>
              </div>
            </div>

            <div className="row mb-2">
          
          <div className="col-xs-12 col-lg-offset-3 col-lg-6">

            <div className="card">
              <div className="card-header sm">
                <div className="typo-strong-175">
                  Required Fields
                </div>
              </div>
              <div className="card-content sm">

                <div className="row mb-2">
                  <div className="col-xs-4">
                    <div className="typo-strong-150">First Name:</div>
                    <input type="text" placeholder="" className="input sm typo-normal-150 w-100" value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                  </div>
                  <div className="col-xs-4">
                    <div className="typo-strong-150">Last Name:</div>
                    <input type="text" placeholder="" className="input sm typo-normal-150 w-100" value={lastName} onChange={(e) => setLastName(e.target.value)} />
                  </div>
                </div>

                <div className="row">
                  <div className="col-xs-4">
                    <div className="typo-strong-150">Email Address:</div>
                    <input type="text" placeholder="" className="input sm typo-normal-150 w-100" value={email} onChange={(e) => setEmail(e.target.value)} />
                    <div className="typo-strong-150 mt-2">Phone Number:</div>
                    <input type="text" placeholder="" className="input sm typo-normal-150 w-100" value={phone} onChange={(e) => setPhone(e.target.value)} />
                  </div>
                  <div className="col-xs-8">
                    <div className="typo-strong-150">Message:</div>
                    <textarea rows="4" placeholder="" className="input sm typo-normal-150 w-100" value={message} onChange={(e) => setMessage(e.target.value)} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="row mb-4">
          <div className="col-xs-12 col-lg-offset-3 col-lg-6">
            {renderPages()}
          </div>
        </div>

            <div className="row">
              <div className="col-xs-12 col-lg-offset-3 col-lg-6 text-end">
              <Button onClick={create} color="blue" text={"Create"} />
              </div>
            </div>
          </>
        )}

        {/* File Import */}
        {importMode === "import" && (
          <>
            <div className="row">
              <div className="col-xs-12 col-lg-offset-3 col-lg-6">
                <span className="typo-strong-200">Import File</span>
              </div>
            </div>

            <div className="row mb-4">
              <div className="col-xs-12 col-lg-offset-3 col-lg-6">
                <div className="card">
                  <div className="card-content sm">
                    <div className="mb-2">
                      <input
                        type="file"
                        accept=".csv,.xls,.xlsx,.json"
                        onChange={handleFileChange}
                        className="input sm typo-normal-150 w-100"
                      />
                      {selectedFile && (
                        <div className="typo-normal-150 mt-2">Selected: {selectedFile.name}</div>
                      )}
                    </div>
                    <div className="d-flex align-items-center mt-3">
                      <div className="typo-normal-150 me-2">Templates:</div>
                      <div className="item grey typo-normal-150 me-2" onClick={() => downloadTemplate('csv')}>Download CSV</div>
                      <div className="item grey typo-normal-150 me-2" onClick={() => downloadTemplate('xlsx')}>Download XLS</div>
                      <div className="item grey typo-normal-150 me-2" onClick={() => downloadTemplate('json')}>Download JSON</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="row">
              <div className="col-xs-12 col-lg-offset-3 col-lg-6 text-end">
                <Button onClick={importFile} color="blue" text={"Import"} />
              </div>
            </div>
          </>
        )}

      </div>
    </>
  );
}

export default ReferralCreate;

